import { IOneTimeInvoiceFormSchemaWithOfferings } from '@monetize/types/app';
import {
  addDaysToDateOnly,
  getNetTermsDays,
  toDateShort,
} from '@monetize/utils/core';
import { useEffect, useRef } from 'react';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import {
  MBox,
  MFormField,
  MText,
  QuoteSelectInput,
} from '../../../../components/Monetize';
import { DatePicker } from '../../../../components/Monetize/DatePicker/DatePicker';
import { BillGroupItemRenderer } from '../../../../components/Monetize/Dropdowns/BillGroupDropdown';
import { AccountLookupPopover } from '../../../../components/Monetize/LookupPopover';
import { MCustomSelect } from '../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { QuoteBasicInfoLine } from '../../../../components/Quotes/QuoteBasicInfoLine';
import { useNetTerms } from '../../../../hooks';
import { useOneTimeInvoice } from './oneTimeInvoiceContext';

const POPOVER_PROPS = {
  positioning: { placement: 'bottom-end', sameWidth: false },
} as const;
const POPOVER_CONTENT_PROPS = { minWidth: '10rem' };

/**
 * First column of the invoice setup card — the account-derived values and the
 * bill group driven header fields.
 */
export const OneTimeInvoiceBasicInvoice = () => {
  const {
    accountId,
    accountName,
    legalEntityName,
    currency,
    billGroups,
    isBillGroupsLoading,
  } = useOneTimeInvoice();
  const { control, watch, setValue } =
    useFormContext<IOneTimeInvoiceFormSchemaWithOfferings>();
  const { errors } = useFormState({ control });

  const selectedNetTerm = watch('netTerms');
  const invoicingDate = watch('invoicingDate');
  const { activeNetTerms, isLoading: isLoadingNetTerms } =
    useNetTerms(selectedNetTerm);

  // Due Date defaults to Invoicing Date + Net Terms and keeps recalculating as
  // either of those change, until the user edits Due Date directly - from then
  // on their value wins and is no longer overwritten.
  const isDueDateManuallySetRef = useRef(false);
  useEffect(() => {
    if (isDueDateManuallySetRef.current || !invoicingDate) {
      return;
    }
    setValue(
      'dueDate',
      addDaysToDateOnly(invoicingDate, getNetTermsDays(selectedNetTerm)),
      { shouldValidate: true },
    );
  }, [invoicingDate, selectedNetTerm, setValue]);

  return (
    <MBox>
      <QuoteBasicInfoLine title="Account">
        <MBox textAlign="right">
          <AccountLookupPopover
            value={accountId}
            displayLabel={accountName}
            hideRecordId
          />
        </MBox>
      </QuoteBasicInfoLine>

      <QuoteBasicInfoLine title="Legal Entity" showRequired={!legalEntityName}>
        <MText>{legalEntityName}</MText>
      </QuoteBasicInfoLine>

      <QuoteBasicInfoLine title="Currency" showRequired={!currency}>
        <MText>{currency}</MText>
      </QuoteBasicInfoLine>

      <QuoteBasicInfoLine
        title="Bill Group"
        inputId="invoice-header-bill-group"
        showRequired
      >
        <MBox mr="-3">
          <MFormField error={errors?.billGroupId}>
            <Controller
              name="billGroupId"
              control={control}
              render={({ field }) => (
                <MCustomSelect
                  id="invoice-header-bill-group"
                  inputTestId="customSelect-billGroupId"
                  MainInputComponent={QuoteSelectInput}
                  placeholder="Select"
                  items={billGroups}
                  itemTitle="name"
                  itemValue="id"
                  renderItemContent={BillGroupItemRenderer}
                  loading={isBillGroupsLoading}
                  isDisabled={isBillGroupsLoading}
                  popOverProps={POPOVER_PROPS}
                  popOverContentProps={POPOVER_CONTENT_PROPS}
                  showContentInInput
                  isLazy
                  {...field}
                />
              )}
            />
          </MFormField>
        </MBox>
      </QuoteBasicInfoLine>

      <QuoteBasicInfoLine title="Invoicing Date" showRequired>
        <MBox mr="-2">
          <MFormField error={errors?.invoicingDate}>
            <Controller
              name="invoicingDate"
              control={control}
              render={({ field: { value, onChange, ...rest } }) => (
                <DatePicker
                  {...rest}
                  triggerType="button"
                  btnText={toDateShort(value || null) || 'Select'}
                  btnProps={{ my: 0, 'data-testid': 'invoicingDateBtn' }}
                  value={value ?? null}
                  onChange={(next) => onChange(next ?? '')}
                />
              )}
            />
          </MFormField>
        </MBox>
      </QuoteBasicInfoLine>

      <QuoteBasicInfoLine
        title="Net Terms"
        inputId="invoice-header-net-terms"
        showRequired
      >
        <MBox mr="-3">
          <MFormField error={errors?.netTerms}>
            <Controller
              name="netTerms"
              control={control}
              render={({ field }) => (
                <MCustomSelect
                  id="invoice-header-net-terms"
                  inputTestId="customSelect-netTerms"
                  MainInputComponent={QuoteSelectInput}
                  placeholder="Select"
                  itemTitle="name"
                  itemValue="value"
                  items={activeNetTerms}
                  loading={!field.value && isLoadingNetTerms}
                  popOverProps={POPOVER_PROPS}
                  popOverContentProps={POPOVER_CONTENT_PROPS}
                  showContentInInput
                  isLazy
                  {...field}
                />
              )}
            />
          </MFormField>
        </MBox>
      </QuoteBasicInfoLine>

      <QuoteBasicInfoLine title="Due Date" showRequired>
        <MBox mr="-2">
          <MFormField error={errors?.dueDate}>
            <Controller
              name="dueDate"
              control={control}
              render={({ field: { value, onChange, ...rest } }) => (
                <DatePicker
                  {...rest}
                  triggerType="button"
                  btnText={toDateShort(value || null) || 'Select'}
                  btnProps={{ my: 0, 'data-testid': 'due-date-btn' }}
                  value={value ?? null}
                  onChange={(next) => {
                    isDueDateManuallySetRef.current = true;
                    onChange(next ?? '');
                  }}
                />
              )}
            />
          </MFormField>
        </MBox>
      </QuoteBasicInfoLine>
    </MBox>
  );
};
